import React, { useState, useRef, useEffect } from 'react';
import { Icon, Platform } from './Icons.jsx';
import { api } from '../services/api/client.js';
import { useContentFeed } from '../hooks/useContentFeed.js';
import { normalizePost } from './preview/normalizePost.js';
import {
  assistantActionLocale,
  assistantActionText,
  assistantActionsForContext,
  assistantTrayText,
  buildAssistantActionDraft,
} from './assistant/actionRegistry.js';

const ASSISTANT_NAME = 'Dathent Assistant';
const ASSISTANT_OAUTH_RETURN_KEY = 'dathent_assistant_social_oauth_return';

const SOCIAL_PLATFORM_LABELS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  threads: 'Threads',
  telegram: 'Telegram',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  x: 'X',
};

const ASSISTANT_COPY = {
  en: {
    created: 'Draft created',
    generationStarted: 'Generation started',
    draftsCreated: 'drafts created',
    left: 'left',
    openDraft: 'Open draft',
    openGeneration: 'Open preview',
    placeholderDefault: 'Ask AI to do anything...',
    placeholderBatch: 'What should the posts be about?',
    placeholderRewrite: 'What should change?',
    placeholderReference: 'Add a topic or paste a link',
    placeholderPost: 'What should the post be about?',
  },
  uk: {
    created: 'Чернетку створено',
    generationStarted: 'Генерацію запущено',
    draftsCreated: 'чернетки',
    left: 'залишилось',
    openDraft: 'Відкрити чернетку',
    openGeneration: 'Відкрити preview',
    placeholderDefault: 'Напиши, що зробити...',
    placeholderBatch: 'Про що мають бути пости?',
    placeholderRewrite: 'Що змінити?',
    placeholderReference: 'Додай тему або встав посилання',
    placeholderPost: 'Про що має бути пост?',
  },
  ru: {
    created: 'Черновик создан',
    generationStarted: 'Генерация запущена',
    draftsCreated: 'черновики',
    left: 'осталось',
    openDraft: 'Открыть черновик',
    openGeneration: 'Открыть preview',
    placeholderDefault: 'Напишите, что сделать...',
    placeholderBatch: 'О чем должны быть посты?',
    placeholderRewrite: 'Что изменить?',
    placeholderReference: 'Добавьте тему или вставьте ссылку',
    placeholderPost: 'О чем должен быть пост?',
  },
  nl: {
    created: 'Concept gemaakt',
    generationStarted: 'Generatie gestart',
    draftsCreated: 'concepten gemaakt',
    left: 'over',
    openDraft: 'Concept openen',
    openGeneration: 'Preview openen',
    placeholderDefault: 'Vraag AI om iets te doen...',
    placeholderBatch: 'Waar moeten de berichten over gaan?',
    placeholderRewrite: 'Wat moet er veranderen?',
    placeholderReference: 'Voeg een onderwerp toe of plak een link',
    placeholderPost: 'Waar moet het bericht over gaan?',
  },
};

const SCREEN_GREETINGS = {
  dashboard: "Your week is on track. 12 posts scheduled, 2 waiting for your review.",
  scheduler: "I see Friday is light. Want me to suggest 2 fillers?",
  content: "You have 47 pieces of content. Anything specific you're looking for?",
  create: "Tell me what you want to make and I'll handle the rest.",
  carousel: "I can rewrite slides, change visual style, or add a CTA slide.",
  editor: "Want me to make this shorter, or adapt it for another platform?",
  social: "Two accounts need attention. Want me to walk you through reconnecting?",
  brand: "Your brand profile shapes every generation. Want to sharpen anything?",
  rules: "Rules tell me how to behave. Try: 'Never post about politics.'",
  billing: "Your plan is based on posts, visuals, brands, and social accounts. Want me to explain the limits?",
  settings: "Anything technical I can help configure?",
  default: "Hi Alex 👋 What can I help with?",
};

const makeMessageId = (prefix) => `${prefix}-${globalThis.crypto?.randomUUID?.() || Date.now()}`;

// Returns the first sentence of `text` trimmed to ~48 characters, or null if
// the text is missing or that first sentence is too long to make a clean
// status pill. Used for AI/locale-driven result-card titles.
const firstShortSentence = (text) => {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  const sentence = trimmed.split(/[.!?\n]/)[0].trim();
  if (!sentence) return null;
  if (sentence.length > 48) return null;
  return sentence;
};
const ASSISTANT_PANEL_MESSAGES_KEY = 'dathent_assistant_panel_messages_v1';

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(reader.error || new Error('Could not read file'));
  reader.readAsDataURL(file);
});

const greetingMessage = (screen) => ({
  id: makeMessageId('greeting'),
  from: 'ai',
  text: SCREEN_GREETINGS[screen] || SCREEN_GREETINGS.default,
});

const loadCachedAssistantPanelMessages = () => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(ASSISTANT_PANEL_MESSAGES_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter(message => message && typeof message === 'object').slice(-50) : [];
  } catch {
    return [];
  }
};

const cacheAssistantPanelMessages = (messages) => {
  if (typeof window === 'undefined' || !hasConversationMessages(messages)) return;
  try {
    window.localStorage.setItem(ASSISTANT_PANEL_MESSAGES_KEY, JSON.stringify(messages.slice(-50)));
  } catch {
    // Best-effort cache only. Server history remains the source of truth.
  }
};

const hasConversationMessages = (messages) => messages.some(message => (
  message.persisted || message.from === 'user' || message.response || message.error
));

const persistedMessageToBubble = (message) => {
  const isAssistant = message.role === 'assistant';
  const response = isAssistant && message.response ? {
    message: message.message,
    intent: message.response.intent,
    requiresConfirmation: Boolean(message.response.requiresConfirmation),
    creditCost: Number(message.response.creditCost || 0),
    freeQuotaUsed: Boolean(message.response.freeQuotaUsed),
    freeMessagesRemaining: message.response.freeMessagesRemaining,
    operationType: message.response.operationType || 'assistant.chat',
    proposedActions: Array.isArray(message.response.proposedActions) ? message.response.proposedActions : [],
    quickReplies: Array.isArray(message.response.quickReplies) ? message.response.quickReplies : [],
    quickReplyGroup: message.response.quickReplyGroup || null,
    safeToExecute: Boolean(message.response.safeToExecute),
    resultCard: message.response.resultCard || null,
    responseLanguage: message.response.responseLanguage || null,
  } : null;

  return {
    id: message.id || makeMessageId(isAssistant ? 'ai' : 'user'),
    from: isAssistant ? 'ai' : 'user',
    text: message.message || '',
    response,
    resultCard: response?.resultCard || null,
    persisted: true,
  };
};

const assistantResponseToBubble = (assistantResponse, fallbackText = 'AI response ready.') => ({
  id: makeMessageId('ai'),
  from: 'ai',
  text: assistantResponse?.message || fallbackText,
  response: assistantResponse ? {
    ...assistantResponse,
    proposedActions: Array.isArray(assistantResponse.proposedActions) ? assistantResponse.proposedActions : [],
  } : null,
  resultCard: assistantResponse?.resultCard || null,
});

const assistantQuickReplies = (response) => Array.isArray(response?.quickReplies)
  ? response.quickReplies.filter(reply => reply && typeof reply === 'object' && (reply.prompt || reply.label)).slice(0, 6)
  : [];

const assistantQuickReplyGroup = (response) => {
  const group = response?.quickReplyGroup;
  if (!group || group.type !== 'platform_picker' || !Array.isArray(group.options)) return null;
  const options = group.options
    .filter(option => option && typeof option === 'object' && option.id && (option.label || option.prompt))
    .slice(0, 8);
  return options.length ? { ...group, options } : null;
};

const quickReplyActionMetadata = (reply) => {
  if (!reply?.params || typeof reply.params !== 'object' || Array.isArray(reply.params)) return null;
  return {
    actionId: reply.actionId || null,
    type: reply.type || null,
    label: reply.label || reply.prompt || null,
    params: reply.params,
  };
};

const assistantUsageText = (response, action = null) => {
  if (!response && !action) return null;
  if (response?.resultCard) return null;
  if (response?.resultCard?.result?.status === 'insufficient_credits') return null;
  const cost = Number(action?.creditCost ?? 0);
  if (cost > 0) return `${cost} ${cost === 1 ? 'quota unit' : 'quota units'}`;
  return null;
};

const assistantLanguage = (value) => ['uk', 'ru', 'en', 'nl'].includes(value) ? value : 'en';

const assistantCopy = (language, key) => ASSISTANT_COPY[assistantLanguage(language)]?.[key] || ASSISTANT_COPY.en[key] || key;

const assistantUiLanguage = () => {
  if (typeof navigator === 'undefined') return 'en';
  const language = String(navigator.language || '').toLowerCase();
  if (language.startsWith('uk')) return 'uk';
  if (language.startsWith('ru')) return 'ru';
  if (language.startsWith('nl')) return 'nl';
  return 'en';
};

const assistantInputPlaceholder = (metadata, language = assistantUiLanguage()) => {
  const id = metadata?.actionId;
  const type = metadata?.type;
  if (id === 'generate_week' || type === 'content.generate_paid_batch') return assistantCopy(language, 'placeholderBatch');
  if (id === 'copy_rewrite_post' || type === 'content.rewrite_for_platform') return assistantCopy(language, 'placeholderRewrite');
  if (id === 'from_reference' || type === 'content.create_post_from_reference') return assistantCopy(language, 'placeholderReference');
  if (id === 'create_post' || type === 'content.generate_single_draft') return assistantCopy(language, 'placeholderPost');
  return assistantCopy(language, 'placeholderDefault');
};

const localizedCredits = (value, language = 'en') => {
  const amount = Number(value || 0);
  if (language === 'uk') return `${amount} ${amount === 1 ? 'одиниця квоти' : amount >= 2 && amount <= 4 ? 'одиниці квоти' : 'одиниць квоти'}`;
  if (language === 'ru') return `${amount} ${amount === 1 ? 'единица квоты' : 'единиц квоты'}`;
  return `${amount} ${amount === 1 ? 'quota unit' : 'quota units'}`;
};

const createdDraftCount = (payload = {}) => {
  if (Array.isArray(payload.createdContentIds)) return payload.createdContentIds.length;
  if (payload.createdContentId) return 1;
  return 0;
};

const createdContentIdsFromAssistantResult = (resultLike) => {
  const payloads = [
    resultLike?.resultCard?.result,
    resultLike?.result,
    resultLike,
  ].filter(Boolean);
  const ids = [];
  payloads.forEach(payload => {
    if (Array.isArray(payload.createdContentIds)) ids.push(...payload.createdContentIds);
    if (Array.isArray(payload.createdDrafts)) ids.push(...payload.createdDrafts.map(draft => draft?.contentId || draft?.content?.id));
    if (payload.createdContentId) ids.push(payload.createdContentId);
    if (payload.contentId) ids.push(payload.contentId);
  });
  return [...new Set(ids.filter(Boolean))];
};

const generationBatchIdFromAssistantResult = (resultLike) => {
  const payloads = [
    resultLike?.resultCard?.result,
    resultLike?.result,
    resultLike,
  ].filter(Boolean);
  for (const payload of payloads) {
    if (typeof payload.batchId === 'string' && payload.batchId) return payload.batchId;
    if (typeof payload.generationBatchId === 'string' && payload.generationBatchId) return payload.generationBatchId;
    const redirectUrl = String(payload.redirectUrl || payload.previewUrl || '');
    const match = redirectUrl.match(/[?&]batch=([^&]+)/);
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch {
        return match[1];
      }
    }
  }
  return null;
};

const assistantResultLanguage = (result, fallback = 'en') => assistantLanguage(
  result?.responseLanguage || result?.assistantResponse?.responseLanguage || result?.result?.responseLanguage || fallback
);

const assistantCreditReceipt = (payload = {}, result = {}, language = 'en') => {
  const charged = Number(payload.creditsCharged ?? result?.creditsCharged ?? 0);
  const balance = payload.creditBalance || result?.creditBalance || null;
  if (charged > 0) {
    const remaining = balance?.remaining;
    return Number.isFinite(Number(remaining)) && Number(remaining) <= 3
      ? `${localizedCredits(Number(remaining), language)} ${assistantCopy(language, 'left')}`
      : null;
  }
  return null;
};

const firstUsableImageUrl = (...sources) => {
  for (const source of sources) {
    if (typeof source === 'string' && /^https?:\/\//i.test(source)) return source;
    if (Array.isArray(source)) {
      const found = firstUsableImageUrl(...source);
      if (found) return found;
    }
  }
  return null;
};

const createdDraftItems = (payload = {}) => {
  if (Array.isArray(payload.createdDrafts) && payload.createdDrafts.length) return payload.createdDrafts;
  if (payload.createdContentId || payload.contentId || payload.draft || payload.content) {
    return [{
      contentId: payload.createdContentId || payload.contentId,
      contentVersionId: payload.contentVersionId,
      platform: payload.platform || payload.content?.platform || payload.draft?.targetPlatform || payload.draft?.platform,
      title: payload.title || payload.content?.title || payload.draft?.title,
      previewText: payload.previewText || payload.content?.previewText || payload.caption || payload.draft?.caption,
      openUrl: payload.openUrl || payload.previewUrl || payload.redirectUrl,
      redirectUrl: payload.redirectUrl,
      mediaReuseStatus: payload.mediaReuseStatus || payload.mediaSummary || payload.draft?.mediaReuseStatus,
      mediaSummary: payload.mediaSummary,
      content: payload.content,
      draft: payload.draft,
    }];
  }
  return [];
};

const platformListLabel = (platforms = []) => {
  const values = [...new Set((platforms || []).filter(Boolean))];
  return values.map(socialPlatformLabel).join(', ');
};

const actionSummary = (action) => {
  if (!action) return 'No action available.';
  const summary = action.summary || action.params?.inputSummary || action.params?.summary;
  return summary || action.type?.split('.').join(' ') || 'Review this action';
};

const actionPlatform = (action) => {
  const params = action?.params || {};
  const first = Array.isArray(params.platforms) ? params.platforms[0] : null;
  return String(params.platform || params.targetPlatform || first || '').toLowerCase() || null;
};

const actionNeedsConfirmation = (response, action) => Boolean(
  action?.requiresConfirmation
  || response?.requiresConfirmation
  || Number(action?.creditCost ?? response?.creditCost ?? 0) > 0
  || ['brand_profile.suggest_update', 'brand_profile.apply_update', 'strategy.suggest_update', 'strategy.apply_update', 'content.rewrite_draft', 'content.generate_single_draft', 'content.collect_reference', 'content.analyze_reference', 'content.generate_paid_batch', 'visual.generate_premium_image', 'scheduler.schedule_post', 'publisher.publish_now'].includes(action?.type)
);

const actionResultView = (action, result, language = 'en') => {
  if (!result) return null;
  const payload = result.result || {};
  const createdCount = createdDraftCount(payload);
  const isGenerationSession = Boolean(payload.batchId || String(payload.redirectUrl || '').includes('batch='));
  const baseMessage = result.message || (result.executed ? 'Review the result before applying anything else.' : 'No changes were made.');
  const message = isGenerationSession
    ? baseMessage
    : createdCount > 0
    ? `${createdCount} ${createdCount === 1 ? 'draft was' : 'drafts were'} saved to your Library. Nothing was scheduled or published.`
    : baseMessage;
  const type = action?.type || result.action?.type;
  const redirectUrl = payload.redirectUrl || payload.firstContentUrl || payload.linkUrl || result.redirectUrl || result.linkUrl;
  if (payload.status === 'insufficient_credits') {
    const labelByLang = { uk: 'Підвищити план', ru: 'Улучшить план', nl: 'Plan upgraden' };
    return {
      message: result.message || payload.message || 'Plan quota is not enough. No changes were made.',
      label: labelByLang[language] || 'Upgrade plan',
      redirectUrl: payload.upgradeUrl || '/app/settings?tab=billing'
    };
  }
  if (redirectUrl) return { message, label: isTelegramUrl(redirectUrl) ? 'Open Telegram' : isOAuthStartUrl(redirectUrl) ? 'Continue OAuth' : payload.batchId ? 'Open generation' : createdCount > 0 ? 'Open first draft' : redirectUrl.startsWith('http') ? 'Open result' : 'Open in app', redirectUrl };
  if (payload.createdContentId) return { message, label: 'Open Library', screen: 'content' };
  if (type === 'content.suggest_ideas') return { message: result.message || 'I prepared content ideas for review.', label: 'View ideas', screen: 'create' };
  if (type === 'visual.suggest_prompt') return { message: result.message || 'I prepared a visual prompt for review.', label: 'View post', screen: 'preview' };
  if (type === 'content.rewrite_draft' || type === 'content.generate_single_draft') return { message, label: 'Open editor', screen: 'editor' };
  if (type === 'strategy.regenerate_content_plan') return { message, label: 'View Content Plan', screen: 'brand' };
  if (type?.startsWith('strategy.') || type === 'brand_profile.suggest_update') return { message, label: 'View strategy', screen: 'brand' };
  if (type === 'content.collect_reference' || type === 'content.analyze_reference') return { message, label: 'View references', screen: 'brand' };
  if (type === 'scheduler.schedule_post') return { message, label: 'Open scheduler', screen: 'scheduler' };
  return { message, label: null, screen: null };
};

const previewPayload = (previewResult) => {
  const result = previewResult?.result || previewResult;
  if (!result) return null;
  if (result.analysis || result.prompt || result.profileUpdate || result.appliedGuidance) return result;
  return result.preview || result.draft || result.asset || result.post || result;
};

const socialPlatformLabel = (platform) => SOCIAL_PLATFORM_LABELS[String(platform || '').toLowerCase()] || String(platform || 'Platform');

const isOAuthStartUrl = (url) => String(url || '').toLowerCase().includes('/oauth/') && String(url || '').toLowerCase().includes('/start');
const isTelegramUrl = (url) => /^https?:\/\/t\.me\//i.test(String(url || '')) || /^https?:\/\/telegram\.me\//i.test(String(url || ''));

const isSocialAccountsPayload = (payload) => Boolean(
  payload
  && (
    Array.isArray(payload.connectedPlatforms)
    || Array.isArray(payload.needsAttentionPlatforms)
    || Array.isArray(payload.notConnectedPlatforms)
    || Array.isArray(payload.availableProviders)
  )
);

const storeAssistantOAuthReturn = ({ url, action, result, platform }) => {
  if (typeof window === 'undefined' || !isOAuthStartUrl(url)) return;
  const payload = result?.result || result || {};
  const context = payload.oauthReturnContext || payload.platformStatus?.oauthReturnContext || {};
  try {
    window.localStorage.setItem(ASSISTANT_OAUTH_RETURN_KEY, JSON.stringify({
      source: 'assistant',
      platform: context.platform || platform || payload.platform || action?.params?.platform || null,
      actionType: context.actionType || action?.type || result?.action?.type || null,
      currentScreen: context.currentScreen || action?.params?.currentScreen || null,
      returnPath: context.returnPath || '/settings?tab=social',
      status: payload.status || null,
      at: Date.now(),
    }));
  } catch {
    // OAuth can continue without local return context.
  }
};

const openResultUrl = (url, onNavigate, context = {}) => {
  if (!url) return;
  storeAssistantOAuthReturn({ ...context, url });
  const internalUrl = internalResultUrl(url);
  if (internalUrl?.startsWith('/api/')) {
    window.location.assign(api.url(internalUrl));
    return;
  }
  if (internalUrl) {
    const [rawPath] = internalUrl.split(/[?#]/);
    const path = rawPath.replace(/^\/app(?=\/|$)/, '');
    // Path-style batch URL: /app/batch/:id (current backend format).
    const pathBatchId = batchIdFromPath(path);
    if (pathBatchId && typeof onNavigate === 'function') {
      onNavigate('batch', { batchId: pathBatchId, keepAssistantOpen: true });
      return;
    }
    // Legacy query-style: /create?batch=:id or /posts/create?batch=:id.
    const batchId = generationBatchIdFromUrl(internalUrl);
    if (batchId && (path === '/create' || path === '/posts/create') && typeof onNavigate === 'function') {
      onNavigate('create', { batchId, keepAssistantOpen: true });
      return;
    }
    // Path-style post URL: /app/post/:id (current canonical) + legacy /content/:id/preview.
    const contentId = contentIdFromPath(path);
    if (contentId && typeof onNavigate === 'function') {
      onNavigate('preview', { contentId, keepAssistantOpen: true });
      return;
    }
    const screen = screenForResultPath(path);
    if (typeof onNavigate === 'function') {
      onNavigate(screen);
    } else {
      window.location.assign(url);
    }
    return;
  }
  window.location.assign(url);
};

const generationBatchIdFromUrl = (url = '') => {
  const match = String(url).match(/[?&]batch=([^&]+)/);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const internalResultUrl = (url) => {
  const value = String(url || '');
  if (value.startsWith('/')) return value;
  if (typeof window === 'undefined') return null;
  try {
    const parsed = new URL(value);
    if (parsed.origin !== window.location.origin) return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

// Path-style batch URL like /batch/gb_abc (after stripping /app prefix).
const batchIdFromPath = (path) => {
  const match = String(path || '').match(/^\/batch\/([^/?#]+)$/i);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

// Canonical /post/:id (current) + legacy /content/:id/preview. Both
// resolve to a single content_item; handleNavigate routes via
// 'preview' + { contentId }.
const contentIdFromPath = (path) => {
  const value = String(path || '');
  const canonical = value.match(/^\/post\/([^/?#]+)$/i);
  if (canonical?.[1]) {
    try { return decodeURIComponent(canonical[1]); } catch { return canonical[1]; }
  }
  const legacy = value.match(/^\/content\/([^/]+)\/preview$/i);
  if (legacy?.[1]) {
    try { return decodeURIComponent(legacy[1]); } catch { return legacy[1]; }
  }
  return null;
};

const contentPreviewIdFromPath = (path) => {
  const match = String(path || '').match(/^\/content\/([^/]+)\/preview$/i);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

const screenForResultPath = (path) => {
  const normalized = String(path || '').replace(/^\//, '') || 'dashboard';
  if (normalized === 'content-plan' || normalized === 'brand') return 'brand';
  if (normalized === 'settings/billing' || normalized === 'billing') return 'billing';
  if (normalized === 'posts/library' || normalized === 'content') return 'content';
  if (normalized === 'posts/create' || normalized === 'create') return 'create';
  if (normalized === 'posts/calendar' || normalized === 'scheduler') return 'scheduler';
  return normalized;
};

const referenceFollowUpPrompt = (prompt, assetId) => `${prompt}\nUse reference asset ${assetId}.`;

const extractFirstHttpUrl = (value = '') => {
  const match = String(value).match(/https?:\/\/[^\s<>()"']+/i);
  return match ? match[0].replace(/[.,;:!?]+$/, '') : null;
};

const isReferenceImageFile = (file) => Boolean(file && /^image\//i.test(file.type || '') && file.size <= 8 * 1024 * 1024);

const assistantReferenceContextFromResult = (attachment = {}, result = {}) => {
  const asset = result?.asset || {};
  const assetId = asset.id || asset.assetId || null;
  const publicUrl = asset.publicUrl || attachment.publicUrl || null;
  if (!assetId && !publicUrl) return null;
  const label = attachment.originalFilename || asset.originalFilename || publicUrl || attachment.type || 'Reference';
  return {
    id: assetId || publicUrl,
    assetId,
    publicUrl,
    type: asset.type || attachment.type || 'reference',
    label: compactReferenceLabel(label),
    saved: Boolean(assetId),
  };
};

const compactReferenceLabel = (value = '') => {
  const text = String(value || 'Reference').replace(/^https?:\/\//i, '').replace(/^www\./i, '').trim();
  return text.length > 34 ? `${text.slice(0, 31)}...` : text;
};

const compactDraftTitle = (value = '') => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text || /^Generating:/i.test(text)) return '';
  return text.length > 92 ? `${text.slice(0, 89).trim()}...` : text;
};

const referenceActionDraftFromContext = (context, kind) => {
  if (!context) return null;
  const params = {
    useContentPlan: true,
    inputSummary: `Use ${context.label} as reference context`,
  };
  if (context.assetId) params.referenceAssetId = context.assetId;
  if (context.publicUrl) params.publicUrl = context.publicUrl;
  if (kind === 'week' || kind === 'month') {
    const monthly = kind === 'month';
    return {
      text: `Create a ${monthly ? 'month' : 'week'} of posts from ${context.label}`,
      metadata: {
        actionId: `reference_${kind}`,
        type: 'content.generate_paid_batch',
        label: monthly ? 'Month from reference' : 'Week from reference',
        params: { ...params, planLengthDays: monthly ? 30 : 7, count: monthly ? 30 : 7, platform: 'instagram' },
      },
    };
  }
  return {
    text: `Create one post from ${context.label}`,
    metadata: {
      actionId: 'reference_post',
      type: 'content.create_post_from_reference',
      label: 'Post from reference',
      params,
    },
  };
};

function AssistantSocialAccountsCard({ payload, result, onNavigate }) {
  if (!isSocialAccountsPayload(payload)) return null;
  const connected = Array.isArray(payload.connectedPlatforms) ? payload.connectedPlatforms : [];
  const attention = Array.isArray(payload.needsAttentionPlatforms) ? payload.needsAttentionPlatforms : [];
  const missing = Array.isArray(payload.notConnectedPlatforms) ? payload.notConnectedPlatforms : [];
  const totalConnected = connected.reduce((sum, item) => sum + Number(item.connectedAccountCount || item.accountCount || 0), 0);

  const renderPlatform = (item, mode) => {
    const platform = item.platform;
    const accounts = Array.isArray(item.accounts) ? item.accounts : [];
    const connectAction = item.connectAction;
    const reconnectAction = item.reconnectAction;
    const primaryAction = mode === 'attention' ? reconnectAction : connectAction;
    const redirectUrl = primaryAction?.redirectUrl;
    const unavailable = primaryAction?.unavailable || (!item.connectable && item.reason ? { reason: item.reason } : null);
    return (
      <div className="assistant-social-row" key={`${mode}-${platform}`}>
        <div className="assistant-social-row-main">
          <Platform name={platform} size={24} />
          <div>
            <div className="assistant-social-platform">{socialPlatformLabel(platform)} · {accounts.length || 0}</div>
            <div className="assistant-social-copy">
              {accounts.length ? accounts.map(account => account.displayName || account.externalAccountId || socialPlatformLabel(platform)).join(', ') : item.reason || 'No account connected'}
            </div>
            {unavailable && <div className="assistant-social-warning">{unavailable.reason || 'Backend flow unavailable.'}</div>}
            {primaryAction?.requiresExecutionForLinkToken && <div className="assistant-social-copy">Ask me to connect Telegram and I’ll create a secure setup link.</div>}
          </div>
        </div>
        <div className="assistant-social-actions">
          {redirectUrl && (
            <button className="assistant-result-link" onClick={() => openResultUrl(redirectUrl, onNavigate, { action: primaryAction, result, platform })}>
              {primaryAction.label || (mode === 'attention' ? 'Reconnect' : accounts.length ? 'Add another' : 'Connect')}
            </button>
          )}
          {mode === 'attention' && connectAction?.redirectUrl && (
            <button className="assistant-secondary-result-link assistant-result-link" onClick={() => openResultUrl(connectAction.redirectUrl, onNavigate, { action: connectAction, result, platform })}>
              Add another
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="assistant-social-card">
      <div className="assistant-social-head">
        <div className="assistant-preview-title">Social accounts</div>
        <span>{totalConnected} connected</span>
      </div>
      {connected.length > 0 && <div className="assistant-social-section"><div className="assistant-social-section-title">Connected</div>{connected.map(item => renderPlatform(item, 'connected'))}</div>}
      {attention.length > 0 && <div className="assistant-social-section"><div className="assistant-social-section-title">Needs attention</div>{attention.map(item => renderPlatform(item, 'attention'))}</div>}
      {missing.length > 0 && <div className="assistant-social-section"><div className="assistant-social-section-title">Not connected</div>{missing.map(item => renderPlatform(item, 'missing'))}</div>}
    </div>
  );
}

function AssistantPreviewCard({ preview, action, onNavigate }) {
  const payload = previewPayload(preview);
  if (!payload) return null;
  if (isSocialAccountsPayload(payload)) return <AssistantSocialAccountsCard payload={payload} action={action} result={preview} onNavigate={onNavigate} />;
  const title = payload.type === 'week_plan_from_reference'
    ? 'Weekly reference plan'
    : payload.type === 'post_from_reference'
      ? 'Reference draft preview'
      : action?.params?.platform ? `${action.params.platform} draft preview` : 'Assistant preview';
  const asset = payload.asset || (payload.id && payload.type ? payload : null);
  const analysis = payload.analysis || payload.profileUpdate || payload.appliedGuidance || asset?.analysis || {};
  const fields = [
    ['Reference', asset?.originalFilename || asset?.publicUrl || (asset?.id || payload.referenceAssetId ? 'Selected reference' : null)],
    ['Reference type', asset?.type || payload.sourceType],
    ['Target', payload.targetPlatform || payload.platform],
    ['Hook', payload.hook || payload.title || payload.headline],
    ['Adapted text', payload.adaptedText || payload.caption || payload.body],
    ['CTA', payload.cta],
    ['Hashtags', Array.isArray(payload.hashtags) ? payload.hashtags.join(' ') : payload.hashtags],
    ['Visual style', analysis.visualStyle],
    ['Colors', Array.isArray(analysis.colors) ? analysis.colors.join(', ') : analysis.colors],
    ['Mood', analysis.mood],
    ['Composition', analysis.composition],
    ['Connection', payload.targetConnection?.note],
  ].filter(([, value]) => value);
  const draftItems = Array.isArray(payload.drafts) ? payload.drafts.slice(0, 6) : [];
  const unavailable = payload.unavailable || preview?.result?.unavailable;
  return (
    <div className="assistant-preview-card">
      <div className="assistant-preview-title">{title}</div>
      {fields.length ? fields.map(([label, value]) => (
        <div className="assistant-preview-field" key={label}>
          <span>{label}</span>
          <p>{String(value)}</p>
        </div>
      )) : <div className="assistant-preview-empty">Preview is ready. Confirm only if this is the result you want.</div>}
      {draftItems.length > 0 && (
        <div className="assistant-draft-list">
          {draftItems.map((draft, index) => (
            <div className="assistant-draft-row" key={`${draft.title || draft.hook || index}`}>
              <span>{draft.angle || `Draft ${index + 1}`}</span>
              <p>{draft.hook || draft.title || draft.caption}</p>
            </div>
          ))}
        </div>
      )}
      {unavailable && <div className="assistant-unavailable">Unavailable: {unavailable.reason || 'Backend capability is not implemented.'}</div>}
    </div>
  );
}

const assistantResultFacts = (payload = {}, result = null) => {
  const facts = [];
  const draftCount = createdDraftCount(payload);
  if (draftCount > 0) facts.push(`${draftCount} ${draftCount === 1 ? 'draft' : 'drafts'} saved to your Library`);
  if (payload.asset?.id) facts.push(payload.asset.analysisStatus ? 'Reference saved with reusable guidance' : 'Reference saved for future use');
  if (payload.brandProfileId) facts.push('Brand Profile guidance updated');
  if (payload.platformStatus?.reason) facts.push(payload.platformStatus.reason);
  return facts;
};

function AssistantResultCard({ result, action, onNavigate, language = 'en' }) {
  const payload = result?.result || {};
  const resultLanguage = assistantResultLanguage(result, language);
  const view = actionResultView(action, result, resultLanguage);
  if (createdDraftCount(payload) > 0) {
    return <AssistantDraftResultCard result={result} action={action} payload={payload} onNavigate={onNavigate} language={resultLanguage} />;
  }
  const facts = assistantResultFacts(payload, result);
  const receipt = assistantCreditReceipt(payload, result, resultLanguage);
  if (!result && !view) return null;
  return (
    <div className="assistant-result-card">
      <div className="assistant-result-text">{view?.message || result?.message || 'Backend returned a result.'}</div>
      {receipt && <div className="assistant-credit-receipt"><Icon name="credit" size={13}/>{receipt}</div>}
      {facts.map((fact, index) => <div className="assistant-result-meta" key={`${fact}-${index}`}>{fact}</div>)}
      {Array.isArray(payload.drafts) && payload.drafts.length > 0 && (
        <div className="assistant-draft-list">
          {payload.drafts.slice(0, 5).map((draft, index) => (
            <div className="assistant-draft-row" key={`${draft.title || draft.hook || index}`}>
              <strong>{draft.title || draft.hook || `Draft ${index + 1}`}</strong>
              <span>{draft.platform || 'platform'} - {draft.format || 'post'}</span>
            </div>
          ))}
        </div>
      )}
      {isSocialAccountsPayload(payload) && <AssistantSocialAccountsCard payload={payload} action={action} result={result} onNavigate={onNavigate} />}
      {payload.unavailable && <div className="assistant-unavailable">Unavailable: {payload.unavailable.reason || 'Not implemented'}</div>}
      {/* For insufficient_credits, render exactly ONE upgrade CTA via the
          view.label button below. The previous dual buttons (View plans +
          Choose plan) duplicated the same destination and looked cluttered.
          When extra-usage top-ups land they'll surface as a secondary
          action inside settings/billing — not as a second button here. */}
      {view?.label && (view.redirectUrl || view.screen) && (
        <button className="assistant-result-link" onClick={() => view.redirectUrl ? openResultUrl(view.redirectUrl, onNavigate, { action, result, platform: payload.platform }) : onNavigate?.(view.screen)}>{view.label}</button>
      )}
    </div>
  );
}

function AssistantDraftResultCard({ result, action, payload, onNavigate, language }) {
  const { content, refreshContent, startGenerationBatch } = useContentFeed();
  const [batchSnapshot, setBatchSnapshot] = useState(null);
  const drafts = createdDraftItems(payload);
  const primaryDraft = drafts[0] || {};
  const batchId = payload.batchId || generationBatchIdFromAssistantResult(result);
  const livePosts = React.useMemo(() => batchId
    ? content.filter(item => item.generationBatchId === batchId).map(item => normalizePost(item.raw || item)).filter(Boolean)
    : [], [content, batchId]);
  const firstLivePost = livePosts[0] || null;
  const draftCount = Math.max(livePosts.length, drafts.length, createdDraftCount(payload));
  const platforms = Array.isArray(payload.platforms) && payload.platforms.length
    ? payload.platforms
    : livePosts.flatMap(post => Object.keys(post.platformVersions || {})).concat(drafts.map(draft => draft.platform || draft.content?.platform || draft.draft?.targetPlatform || draft.draft?.platform)).filter(Boolean);
  const isGenerationSession = Boolean(batchId || String(payload.redirectUrl || '').includes('batch='));
  // Prefer the first short sentence of the backend's localized action message
  // over the legacy hardcoded "Generation started" copy — that way the card
  // title speaks the user's language and matches whatever the backend AI
  // actually wrote ("Опублікую перший одразу", "Publishing..."), instead of
  // showing a stale English string when the response is in Polish/Russian/etc.
  // Falls back to the hardcoded copy only when the backend message is missing
  // or too long to fit as a title.
  const fallbackTitle = isGenerationSession ? assistantCopy(language, 'generationStarted') : draftCount > 1 ? `${draftCount} ${assistantCopy(language, 'draftsCreated')}` : assistantCopy(language, 'created');
  const title = firstShortSentence(payload?.shortStatus) || firstShortSentence(payload?.message) || fallbackTitle;
  const preview = firstLivePost?.caption || firstLivePost?.body || primaryDraft.previewText || primaryDraft.content?.previewText || primaryDraft.caption || primaryDraft.draft?.caption || payload.previewText || payload.caption || result?.message;
  const platform = primaryDraft.platform || payload.platform || payload.content?.platform || payload.draft?.targetPlatform || payload.draft?.platform || action?.params?.platform;
  const mediaStatus = primaryDraft.mediaReuseStatus || primaryDraft.mediaSummary || payload.mediaReuseStatus || payload.mediaSummary || payload.draft?.mediaReuseStatus || {};
  const thumbnailUrl = firstUsableImageUrl(
    ...livePosts.map(post => post?.mediaAssets?.map(asset => asset?.publicUrl)),
    ...livePosts.map(post => post?.slides?.map(slide => slide?.imageUrl)),
    mediaStatus.imageUrl,
    mediaStatus.thumbnailUrl,
    mediaStatus.mediaUrls,
    ...drafts.map(draft => draft?.mediaReuseStatus?.imageUrl),
    ...drafts.map(draft => draft?.mediaReuseStatus?.mediaUrls),
    payload.mediaSummary?.thumbnailUrl,
    payload.draft?.mediaUrls,
    payload.mediaUrls
  );
  // For batch generation results we want the primary CTA to land on the
  // batch progress screen (/app/batch/:id) where the user sees every
  // language/platform tile, NOT a single post preview. The legacy logic
  // tried draft-level URLs first which routed to the first content_item's
  // /content/:id/preview and looked like "only one post". Batch-level
  // payload.redirectUrl (set by the backend to /app/batch/:id) now wins
  // when this is a generation session; single-draft results fall through
  // to the draft-level URLs as before.
  const openUrl = isGenerationSession
    ? (payload.redirectUrl || payload.openUrl || payload.previewUrl || primaryDraft.openUrl || primaryDraft.previewUrl || primaryDraft.redirectUrl || (payload.createdContentId ? `/app/post/${encodeURIComponent(payload.createdContentId)}` : null))
    : (primaryDraft.openUrl || primaryDraft.previewUrl || primaryDraft.redirectUrl || payload.openUrl || payload.previewUrl || payload.redirectUrl || (payload.createdContentId ? `/app/post/${encodeURIComponent(payload.createdContentId)}` : null));
  const draftTitle = compactDraftTitle(firstLivePost?.title)
    || compactDraftTitle(firstLivePost?.hook)
    || compactDraftTitle(firstLivePost?.caption || firstLivePost?.body)
    || compactDraftTitle(primaryDraft.title)
    || compactDraftTitle(payload.title || payload.content?.title || payload.draft?.title)
    || action?.title
    || title;
  const platformText = platformListLabel(platforms.length ? platforms : [platform].filter(Boolean));
  const batchStatus = assistantBatchStatus(batchSnapshot?.batch, livePosts);
  const thumbnailGrid = livePosts.map(post => firstUsableImageUrl(post.mediaAssets?.map(asset => asset?.publicUrl), post.slides?.map(slide => slide?.imageUrl))).filter(Boolean);

  useEffect(() => {
    if (!batchId) return undefined;
    let stopped = false;
    let intervalId = null;
    const poll = async () => {
      try {
        const batchResult = await api.content.getBatch(batchId);
        if (stopped) return;
        setBatchSnapshot(batchResult || null);
        if (batchResult?.batch) startGenerationBatch?.(batchResult.batch, []);
        await refreshContent?.({ background: true, refreshMetrics: false });
        if (assistantBatchTerminal(batchResult?.batch, livePosts) && intervalId) {
          window.clearInterval(intervalId);
          intervalId = null;
        }
      } catch {
        // Result cards are best-effort; the batch preview remains the source of truth.
      }
    };
    poll();
    intervalId = window.setInterval(poll, 4000);
    return () => {
      stopped = true;
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [batchId]);

  const quotaNotices = Array.isArray(payload?.quotaNotices) ? payload.quotaNotices : [];
  const upgradeHref = typeof payload?.upgradeUrl === 'string' ? payload.upgradeUrl : null;

  return (
    <div className="assistant-result-card assistant-draft-result-card">
      <div className="assistant-draft-result-head">
        <div className="assistant-result-status"><Icon name="checkCircle" size={16}/>{title}</div>
        {isGenerationSession && <span className={'assistant-generation-status status-' + batchStatus.state}>{batchStatus.label}</span>}
      </div>
      {quotaNotices.length > 0 && (
        <div className="assistant-quota-notices">
          {quotaNotices.map((notice, index) => (
            <div
              key={`${notice?.metric || 'quota'}-${index}`}
              className={'assistant-quota-banner ' + (notice?.kind === 'downgrade' ? 'is-downgrade' : '')}
            >
              <span className="assistant-quota-icon">
                <Icon name={notice?.kind === 'downgrade' ? 'image' : 'alert'} size={13} />
              </span>
              <div className="assistant-quota-body">{notice?.message || 'Plan limit reached.'}</div>
              {upgradeHref && (
                <button
                  type="button"
                  className="assistant-quota-cta"
                  onClick={() => openResultUrl(upgradeHref, onNavigate, { action, result })}
                >
                  {language === 'uk' ? 'Покращити' : language === 'ru' ? 'Улучшить' : 'Upgrade'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="assistant-draft-preview-card">
        {thumbnailUrl ? (
          <img className="assistant-draft-thumb" src={thumbnailUrl} alt="" loading="lazy" decoding="async" />
        ) : (
          <div className="assistant-draft-thumb is-empty"><Icon name="image" size={18}/></div>
        )}
        <div className="assistant-draft-copy">
          <div className="assistant-draft-title">{draftTitle}</div>
          {platformText && <div className="assistant-draft-platform-chip">{platformText}</div>}
          <div className="assistant-draft-preview">{preview}</div>
        </div>
      </div>
      {isGenerationSession && thumbnailGrid.length > 1 && (
        <div className="assistant-generation-thumbs">
          {thumbnailGrid.slice(0, 3).map((url, index) => <img key={`${url}-${index}`} src={url} alt="" loading="lazy" decoding="async" />)}
          {draftCount > 3 && <span>+{draftCount - 3}</span>}
        </div>
      )}
      {/* Per-draft list. Show only when there's something useful to display
          — i.e. drafts that already have real content (not the placeholder
          title "Generating: …"). While the batch is still generating we
          collapse the list so users don't see redundant placeholder rows
          ("Draft 1", "Draft 2"). The batch progress pill at the top of the
          card already communicates that generation is in flight. */}
      {drafts.length > 1 && drafts.some((d) => d.caption || d.body || d.previewText || (d.title && !/^Generating[:\s]/i.test(String(d.title)))) && (
        <div className="assistant-draft-result-list">
          {drafts.slice(0, 6).map((draft, index) => {
            const draftUrl = draft.openUrl || draft.previewUrl || draft.redirectUrl || (draft.contentId ? `/app/post/${encodeURIComponent(draft.contentId)}` : null);
            const body = draft.previewText || draft.caption || draft.body;
            return (
              <button
                type="button"
                className="assistant-draft-result-item"
                key={draft.contentId || `${draft.platform}-${index}`}
                onClick={() => draftUrl && openResultUrl(draftUrl, onNavigate, { action, result, platform: draft.platform })}
                disabled={!draftUrl}
              >
                <span>{socialPlatformLabel(draft.platform) || `Variant ${index + 1}`}</span>
                <p>{body || assistantCopy(language, 'openDraft')}</p>
              </button>
            );
          })}
        </div>
      )}
      {/* Single primary action: open the batch progress screen (or live
          post preview if there's only one). "Change schedule" was redundant
          since the same target screen lets the user reschedule directly. */}
      <div className="assistant-result-actions">
        {openUrl && (
          <button
            className="assistant-result-link assistant-primary-result-link"
            onClick={() => openResultUrl(openUrl, onNavigate, { action, result, platform })}
          >
            {assistantCopy(language, isGenerationSession ? 'openGeneration' : 'openDraft')}
          </button>
        )}
      </div>
    </div>
  );
}

function assistantBatchStatus(batch, posts) {
  if (posts.some(post => post.status === 'failed') || batch?.status === 'failed' || batch?.status === 'partial_failed') return { state: 'failed', label: 'Needs retry' };
  if (!posts.length || posts.some(post => post.status === 'generating')) return { state: 'generating', label: 'Generating' };
  if (posts.some(post => post.generationStage === 'visualizing')) return { state: 'visualizing', label: 'Visualizing' };
  if (posts.some(post => post.status === 'scheduled') || batch?.scheduleMode === 'schedule') return { state: 'scheduled', label: 'Best times set' };
  if (posts.length && posts.every(post => ['ready', 'published', 'scheduled'].includes(post.status))) return { state: 'ready', label: 'Ready' };
  if (posts.some(post => post.mediaAssets?.length)) return { state: 'visualizing', label: 'Visuals ready' };
  return { state: 'generating', label: 'Generating' };
}

function assistantBatchTerminal(batch, posts) {
  if (batch && !['queued', 'generating', 'reserved'].includes(batch.status)) return true;
  return posts.length > 0 && posts.every(post => ['ready', 'scheduled', 'published', 'failed'].includes(post.status));
}

const friendlyAssistantError = (error) => {
  if (error?.status === 401) return 'Please sign in again before using Ask AI.';
  if (error?.status === 429) return error.message || 'Ask AI is rate limited right now. Try again shortly.';
  return 'Ask AI could not reach the assistant backend. Your message is still here, and you can retry.';
};

function AssistantActionCard({ action, response, selectedEntity, state, onPreview, onConfirm, onCancel, onNavigate, language = 'en' }) {
  void selectedEntity;
  void onPreview;
  const usage = assistantUsageText(response, action);
  const needsConfirmation = actionNeedsConfirmation(response, action);
  const canConfirm = Boolean(action?.id);
  const draftAction = { ...action, params: action?.params || {} };
  const isBusy = state?.status === 'confirming';
  const isConfirmed = state?.status === 'confirmed';
  const isCancelled = state?.status === 'cancelled';
  const isError = state?.status === 'error';
  const result = state?.result;
  const preview = state?.preview;
  const primaryLabel = needsConfirmation ? 'Confirm' : (action?.ui?.primaryLabel || 'Run');
  const socialPlatform = action?.type?.startsWith('social.') ? actionPlatform(action) : null;

  return (
    <div className="assistant-action-card assistant-action-card-compact">
      {!isConfirmed && !isCancelled && (
        <>
          {socialPlatform ? (
            <div className="assistant-action-social-head">
              <Platform name={socialPlatform} size={28} />
              <div className="assistant-action-summary">{actionSummary(action)}</div>
            </div>
          ) : (
            <div className="assistant-action-summary">{actionSummary(action)}</div>
          )}
          {usage && <div className="assistant-action-cost">Uses {usage}</div>}
        </>
      )}
      {!isConfirmed && !isCancelled && (
        <div className="assistant-confirm-actions">
          <button className="assistant-confirm-btn" onClick={() => onConfirm(draftAction)} disabled={isBusy || !canConfirm}>
            {isBusy ? 'Working...' : primaryLabel}
          </button>
          <button className="assistant-cancel-btn" onClick={() => onCancel(draftAction)} disabled={isBusy}>
            {action?.ui?.secondaryLabel || 'Cancel'}
          </button>
          {!canConfirm && <div className="assistant-action-error">Backend action ID is missing. Ask Dathent Assistant to prepare it again.</div>}
        </div>
      )}
      {preview && <AssistantPreviewCard preview={preview} action={draftAction} onNavigate={onNavigate} />}
      {isConfirmed && <AssistantResultCard result={result} action={draftAction} onNavigate={onNavigate} language={language} />}
      {isCancelled && <div className="assistant-cancelled">Cancelled. No changes made.</div>}
      {isError && <div className="assistant-action-error">{state.message || 'Confirmation failed. No changes were made.'}</div>}
    </div>
  );
}

function AssistantPlatformPicker({ group, onSelect }) {
  const [selected, setSelected] = useState([]);
  if (!group?.options?.length) return null;

  // option.id may be a compound key like "telegram:soc_xxx" for per-account
  // rows; the underlying platform always lives in option.params.platforms[0].
  const platformOfOption = (option) => option?.params?.platforms?.[0] || option?.id || null;

  const optionIds = group.options.map(option => option.id);
  const selectedOptions = group.options.filter(option => selected.includes(option.id));
  const submitSelection = (ids) => {
    const picked = group.options.filter(option => ids.includes(option.id));
    if (!picked.length) return;
    const labels = picked.map(option => option.label || option.prompt).filter(Boolean);
    // Merge platforms (deduped) and targetAccountIds across selected rows so
    // the user can mix per-account picks (e.g. Telegram channel A + Telegram
    // channel B, or Telegram channel A + Instagram).
    const platforms = Array.from(new Set(picked.flatMap(option => option.params?.platforms || [option.id]).filter(Boolean)));
    const targetAccountIds = Array.from(new Set(picked.flatMap(option => option.params?.targetAccountIds || []).filter(Boolean)));
    const params = { platforms };
    if (targetAccountIds.length) params.targetAccountIds = targetAccountIds;
    onSelect?.({
      label: labels.join(', '),
      prompt: labels.join(', '),
      kind: 'platform',
      params,
    });
  };
  const toggleOption = (option) => {
    if (!group.multiple) {
      submitSelection([option.id]);
      return;
    }
    setSelected(current => current.includes(option.id)
      ? current.filter(id => id !== option.id)
      : [...current, option.id]);
  };

  return (
    <div className="assistant-platform-picker">
      <div className="assistant-platform-picker-title">{group.title || 'Pick channels'}</div>
      <div className="assistant-platform-options" role="group" aria-label={group.title || 'Pick channels'}>
        {group.options.map(option => {
          const active = selected.includes(option.id);
          const platform = platformOfOption(option);
          return (
            <button
              key={option.id}
              type="button"
              className={'assistant-platform-option ' + (active ? 'is-active' : '')}
              aria-pressed={active}
              onClick={() => toggleOption(option)}
            >
              <Platform name={platform} size={18} />
              {option.label || option.prompt}
            </button>
          );
        })}
      </div>
      {group.multiple && (
        <div className="assistant-platform-picker-actions">
          <button type="button" className="assistant-platform-submit" disabled={!selectedOptions.length} onClick={() => submitSelection(selected)}>
            {group.submitLabel || 'Use selected'}
          </button>
          <button type="button" className="assistant-platform-all" onClick={() => submitSelection(optionIds)}>
            {group.allLabel || 'All shown'}
          </button>
        </div>
      )}
    </div>
  );
}

function AssistantBubble({ message, onRetry, onCancel, onPreviewAction, onConfirmAction, onQuickReply, selectedEntity, onNavigate, variant = 'legacy' }) {
  const actions = Array.isArray(message.response?.proposedActions) ? message.response.proposedActions : [];
  const quickReplies = assistantQuickReplies(message.response);
  const quickReplyGroup = assistantQuickReplyGroup(message.response);
  const usage = actions.length ? null : assistantUsageText(message.response);
  const language = assistantLanguage(message.response?.responseLanguage);
  const isAi = message.from === 'ai';
  const canStream = variant === 'panel' && isAi && !message.persisted && !message.error && Boolean(message.text);
  const baseClass = variant === 'panel'
    ? `as-bubble ${isAi ? 'as-bubble-bot' : 'as-bubble-user'}${message.error ? ' is-error' : ''}`
    : `chatbar-bubble ${isAi ? 'is-ai' : 'is-u'}${message.error ? ' is-error' : ''}`;

  return (
    <div className={baseClass}>
      <div>{canStream ? <StreamedText text={message.text} enabled /> : message.text}</div>
      {usage && <div className="assistant-usage-pill">{Number(message.response?.creditCost || 0) > 0 ? `Quota: ${usage}` : usage}</div>}
      {message.error && message.retryText && (
        <button className="assistant-inline-btn" onClick={() => onRetry(message.retryText)}>
          Retry
        </button>
      )}
      {message.resultCard && <AssistantResultCard result={message.resultCard} action={message.resultCard.action} onNavigate={onNavigate} language={language} />}
      {quickReplyGroup && <AssistantPlatformPicker group={quickReplyGroup} onSelect={onQuickReply} />}
      {quickReplies.length > 0 && (
        <div className="assistant-quick-replies">
          {quickReplies.map((reply, index) => (
            <button
              type="button"
              className={'assistant-quick-reply ' + (reply.kind ? `is-${reply.kind}` : '')}
              key={`${reply.label || reply.prompt}-${index}`}
              onClick={() => onQuickReply?.(reply)}
            >
              {reply.label || reply.prompt}
            </button>
          ))}
        </div>
      )}
      {actions.map((action, index) => (
        <AssistantActionCard
          key={`${action.type}-${index}`}
          action={action}
          response={message.response}
          selectedEntity={selectedEntity}
          state={message.actionStates?.[index]}
          onPreview={(draftAction) => onPreviewAction(message.id, index, draftAction)}
          onConfirm={(draftAction) => onConfirmAction(message.id, index, draftAction)}
          onCancel={() => onCancel(message.id, index)}
          onNavigate={onNavigate}
          language={language}
        />
      ))}
      {message.response?.safeToExecute === false && actions.length === 0 && (
        <div className="assistant-cancelled">Unsupported or unsafe action. No changes made.</div>
      )}
    </div>
  );
}

function AssistantAttachmentModal({ open, onClose, onUpload }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  if (!open) return null;

  const addFiles = (value) => {
    const next = Array.from(value || []).filter(isReferenceImageFile).slice(0, 8);
    setError(next.length ? '' : 'Choose image files only.');
    setFiles(existing => [...existing, ...next].slice(0, 8));
  };

  const uploadFile = async () => {
    if (!files.length) {
      setError('Choose at least one image.');
      return;
    }
    setUploading(true);
    setError('');
    try {
      for (const file of files) {
        const dataUrl = await fileToDataUrl(file);
        await onUpload({
          type: 'product_image',
          attachmentPurpose: 'product_image',
          dataUrl,
          originalFilename: file.name,
          mimeType: file.type || 'image/png',
          notes: 'Image added by user',
        });
      }
      setFiles([]);
      onClose();
    } catch (uploadError) {
      setError(uploadError?.message || 'Upload failed. No image was saved.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="assistant-attach-scrim" role="presentation" onMouseDown={onClose}>
      <div className="assistant-attach-modal" role="dialog" aria-modal="true" aria-labelledby="assistant-attach-title" onMouseDown={event => event.stopPropagation()}>
        <div className="assistant-attach-head">
          <div>
            <div className="assistant-action-eyebrow">Images</div>
            <h3 id="assistant-attach-title">Add images</h3>
            <p>Drop images here or choose files. Links can be pasted directly into your message.</p>
          </div>
          <button className="assistant-attach-close" onClick={onClose} aria-label="Close attachment options"><Icon name="x" size={16}/></button>
        </div>
        <div
          className="assistant-file-upload-card assistant-image-dropzone"
          onDrop={event => {
            event.preventDefault();
            addFiles(event.dataTransfer?.files);
          }}
          onDragOver={event => event.preventDefault()}
        >
          <Icon name="image" size={22}/>
          <div className="assistant-form-title">Drop images here</div>
          <p>PNG, JPG, WebP, or GIF up to 8 MB each.</p>
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={event => addFiles(event.target.files)} />
          {files.length > 0 && (
            <div className="assistant-image-chip-row">
              {files.map((file, index) => <span className="assistant-image-chip" key={`${file.name}-${index}`}>{file.name}</span>)}
            </div>
          )}
          {error && <div className="assistant-action-error">{error}</div>}
          <button className="assistant-confirm-btn" onClick={uploadFile} disabled={uploading || !files.length}>{uploading ? 'Adding...' : 'Use images'}</button>
        </div>
      </div>
    </div>
  );
}

function AssistantActionTray({ currentScreen, selectedEntity, open, onSelect }) {
  const locale = assistantActionLocale();
  const actions = assistantActionsForContext({ currentScreen, selectedEntity });

  if (!open || !actions.length) return null;

  return (
    <div className="assistant-action-tray assistant-action-menu">
      <div className="assistant-action-tray-head">
        <div>
          <strong>{assistantTrayText('tray.title', locale)}</strong>
          <span>{assistantTrayText('tray.subtitle', locale)}</span>
        </div>
      </div>
      <div className="assistant-action-options" role="listbox" aria-label={assistantTrayText('tray.title', locale)}>
        {actions.map(action => (
          <button
            key={action.id}
            type="button"
            className="assistant-action-option"
            onClick={() => onSelect?.(buildAssistantActionDraft(action))}
          >
            <span className="assistant-action-option-icon"><Icon name={action.icon || 'sparkles'} size={14}/></span>
            <span>
              <strong>{assistantActionText(action, 'label', locale)}</strong>
              <small>{assistantActionText(action, 'description', locale)}</small>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AssistantReferenceChip({ context, onClear, onUse }) {
  if (!context) return null;
  return (
    <div className="assistant-reference-chip">
      <span className="assistant-reference-chip-main"><Icon name="image" size={11}/>{context.label}</span>
      <span className="assistant-reference-saved">Image added</span>
      <button type="button" className="assistant-reference-clear" onClick={onClear} aria-label="Clear reference"><Icon name="x" size={10}/></button>
    </div>
  );
}

async function sendAssistantMessage({
  text,
  currentScreen,
  selectedEntity,
  actionMetadata = null,
  setMessages,
  setInput,
  setExpanded,
  setThinking,
  appendUser = true,
  onAssistantResult,
}) {
  const trimmed = text.trim();
  if (!trimmed) return;

  if (appendUser) {
    setMessages(messages => [...messages, { id: makeMessageId('user'), from: 'user', text: trimmed }]);
    setInput('');
  }
  setExpanded?.(true);
  setThinking(true);

  try {
    const response = await api.assistant.chat({
      message: trimmed,
      currentScreen,
      selectedEntity: selectedEntity || null,
      actionMetadata,
    });
    setMessages(messages => [...messages, {
      id: makeMessageId('ai'),
      from: 'ai',
      text: response?.message || 'I received your message, but the assistant returned an empty response.',
      response,
      resultCard: response?.resultCard || null,
    }]);
    notifyAssistantResult(onAssistantResult, response);
  } catch (error) {
    setMessages(messages => [...messages, {
      id: makeMessageId('error'),
      from: 'ai',
      text: friendlyAssistantError(error),
      error: true,
      retryText: trimmed,
    }]);
  } finally {
    setThinking(false);
  }
}

function notifyAssistantResult(callback, result) {
  if (typeof callback !== 'function') return;
  Promise.resolve(callback(result)).catch(() => {});
}

function useStreamedText(text, { speedMs = 14, enabled = true } = {}) {
  const [shown, setShown] = useState(enabled ? '' : text);
  const [done, setDone] = useState(!enabled);
  useEffect(() => {
    if (!enabled) { setShown(text); setDone(true); return undefined; }
    setShown(''); setDone(false);
    if (!text) { setDone(true); return undefined; }
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        setDone(true);
      }
    }, speedMs);
    return () => clearInterval(id);
  }, [text, speedMs, enabled]);
  return { shown, done };
}

function StreamedText({ text, enabled }) {
  const { shown, done } = useStreamedText(text || '', { enabled });
  const lines = (shown || '').split('\n');
  return (
    <>
      {lines.map((line, i) => (
        <span key={i}>{line}{i < lines.length - 1 && <br/>}</span>
      ))}
      {!done && <span className="as-cursor"/>}
    </>
  );
}

const ASSISTANT_QUICK_ACTIONS = {
  en: [
    { id: 'generate',  icon: 'sparkles', label: 'Generate a post',     prompt: 'Generate a post about' },
    { id: 'connect',   icon: 'link',     label: 'Connect a network',   prompt: 'Connect a social network' },
    { id: 'metrics',   icon: 'eye',      label: "This week's metrics", prompt: "How did this week's posts perform?" },
    { id: 'search',    icon: 'search',   label: 'Find a post',         prompt: 'Find posts about' },
    { id: 'plan',      icon: 'target',   label: 'Tune content plan',   prompt: 'Rebalance my content pillars' },
    { id: 'schedule',  icon: 'calendar', label: 'Reschedule a post',   prompt: 'Reschedule a post' },
  ],
  uk: [
    { id: 'generate',  icon: 'sparkles', label: 'Створити пост',       prompt: 'Створи пост про' },
    { id: 'connect',   icon: 'link',     label: 'Підключити мережу',   prompt: 'Підключити соцмережу' },
    { id: 'metrics',   icon: 'eye',      label: 'Метрики тижня',       prompt: 'Як цей тиждень виступив?' },
    { id: 'search',    icon: 'search',   label: 'Знайти пост',         prompt: 'Знайди пости про' },
    { id: 'plan',      icon: 'target',   label: 'Налаштувати план',    prompt: 'Перерозподіли пілари контенту' },
    { id: 'schedule',  icon: 'calendar', label: 'Перенести пост',      prompt: 'Перенеси пост' },
  ],
  ru: [
    { id: 'generate',  icon: 'sparkles', label: 'Создать пост',        prompt: 'Создай пост о' },
    { id: 'connect',   icon: 'link',     label: 'Подключить сеть',     prompt: 'Подключи соцсеть' },
    { id: 'metrics',   icon: 'eye',      label: 'Метрики недели',      prompt: 'Как прошла эта неделя?' },
    { id: 'search',    icon: 'search',   label: 'Найти пост',          prompt: 'Найди посты про' },
    { id: 'plan',      icon: 'target',   label: 'Настроить план',      prompt: 'Перебалансируй пилары контента' },
    { id: 'schedule',  icon: 'calendar', label: 'Перенести пост',      prompt: 'Перенеси пост' },
  ],
  nl: [
    { id: 'generate',  icon: 'sparkles', label: 'Maak een post',        prompt: 'Maak een post over' },
    { id: 'connect',   icon: 'link',     label: 'Verbind een netwerk',  prompt: 'Verbind een sociaal netwerk' },
    { id: 'metrics',   icon: 'eye',      label: 'Statistieken deze week', prompt: 'Hoe presteerden de posts deze week?' },
    { id: 'search',    icon: 'search',   label: 'Zoek een post',        prompt: 'Zoek posts over' },
    { id: 'plan',      icon: 'target',   label: 'Plan bijstellen',      prompt: 'Herbalanceer mijn content pillars' },
    { id: 'schedule',  icon: 'calendar', label: 'Een post verplaatsen', prompt: 'Verplaats een post' },
  ],
};

function AssistantQuickChips({ onPick, language = 'en' }) {
  const actions = ASSISTANT_QUICK_ACTIONS[language] || ASSISTANT_QUICK_ACTIONS.en;
  const label = language === 'uk' ? 'Швидкі дії' : language === 'ru' ? 'Быстрые действия' : language === 'nl' ? 'Snelle acties' : 'Quick actions';
  return (
    <div className="as-chips">
      <div className="as-chips-l">{label}</div>
      <div className="as-chips-row">
        {actions.map(action => (
          <button key={action.id} type="button" className="as-chip" onClick={() => onPick(action)}>
            <Icon name={action.icon} size={13}/>
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function pendingActionMetadataForMessage(text, pendingActionMetadata, options = {}) {
  if (Object.prototype.hasOwnProperty.call(options, 'actionMetadata')) return options.actionMetadata;
  if (!pendingActionMetadata) return null;
  return shouldDropPendingActionMetadata(text, pendingActionMetadata) ? null : pendingActionMetadata;
}

function shouldDropPendingActionMetadata(text, metadata) {
  if (!metadata || !/https?:\/\//i.test(text)) return false;
  const normalized = text.toLowerCase();
  const asksForPost = /\b(post|draft|caption)\b|пост|допис/i.test(normalized);
  const asksForPlan = /\b(week|weekly|month|monthly|7|30)\b|тиж|місяц|місяч|семь|тридц/i.test(normalized);
  return asksForPost && !asksForPlan && metadata.type !== 'content.create_post_from_reference';
}

export const AIChatBar = ({ currentScreen, selectedEntity = null, expanded, setExpanded, onVoice, sidebarCollapsed }) => {
  const [messages, setMessages] = useState(() => [greetingMessage(currentScreen)]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [actionTrayOpen, setActionTrayOpen] = useState(false);
  const [pendingActionMetadata, setPendingActionMetadata] = useState(null);
  const [referenceContext, setReferenceContext] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadAssistantHistory() {
      try {
        const result = await api.assistant.messages({ limit: 50 });
        const history = Array.isArray(result?.messages)
          ? result.messages.map(persistedMessageToBubble).filter(message => message.text)
          : [];
        if (!cancelled) {
          setMessages(history.length ? history : [greetingMessage(currentScreen)]);
        }
      } catch {
        if (!cancelled) {
          setMessages([greetingMessage(currentScreen)]);
        }
      }
    }

    loadAssistantHistory();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setMessages(existing => hasConversationMessages(existing) ? existing : [greetingMessage(currentScreen)]);
  }, [currentScreen]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking, expanded]);

  const send = (txt, options = {}) => {
    const trimmed = txt.trim();
    const actionMetadata = pendingActionMetadataForMessage(trimmed, pendingActionMetadata, options);
    setActionTrayOpen(false);
    if (trimmed) setPendingActionMetadata(null);
    return sendAssistantMessage({
      text: txt,
      currentScreen,
      selectedEntity,
      actionMetadata,
      setMessages,
      setInput,
      setExpanded,
      setThinking,
      appendUser: options.appendUser !== false,
    });
  };

  const sendQuickReply = (reply) => {
    const text = reply?.prompt || reply?.label || '';
    const actionMetadata = quickReplyActionMetadata(reply);
    return actionMetadata ? send(text, { actionMetadata }) : send(text);
  };

  const insertActionDraft = (draft) => {
    if (!draft?.metadata) return;
    setPendingActionMetadata(draft.metadata || null);
    setActionTrayOpen(false);
    setExpanded?.(true);
    window.requestAnimationFrame?.(() => inputRef.current?.focus());
  };

  const useReferenceContext = (kind) => {
    const draft = referenceActionDraftFromContext(referenceContext, kind);
    if (draft) insertActionDraft(draft);
  };

  const cancelConfirmation = (id) => {
    setMessages(messages => messages.map(message => message.id === id ? { ...message, cancelled: true } : message));
  };

  const setActionState = (messageId, actionIndex, state) => {
    setMessages(messages => messages.map(message => {
      if (message.id !== messageId) return message;
      const actionStates = { ...(message.actionStates || {}) };
      actionStates[actionIndex] = { ...(actionStates[actionIndex] || {}), ...state };
      return { ...message, actionStates };
    }));
  };

  const previewAction = async (messageId, actionIndex, action) => {
    setActionState(messageId, actionIndex, { status: 'previewing' });
    try {
      const preview = await api.assistant.previewAction({ action });
      setActionState(messageId, actionIndex, { status: 'previewed', preview });
    } catch (error) {
      setActionState(messageId, actionIndex, { status: 'error', message: friendlyAssistantError(error) });
    }
  };

  const confirmAction = async (messageId, actionIndex, action) => {
    setActionState(messageId, actionIndex, { status: 'confirming' });
    try {
      const result = await api.assistant.confirmAction({ action, confirmed: true });
      if (result?.assistantResponse) {
        setActionState(messageId, actionIndex, { status: 'confirmed', result: null });
        setMessages(messages => [...messages, assistantResponseToBubble(result.assistantResponse, result.message)]);
        return;
      }
      setActionState(messageId, actionIndex, { status: 'confirmed', result });
    } catch (error) {
      setActionState(messageId, actionIndex, { status: 'error', message: friendlyAssistantError(error) });
    }
  };

  const cancelAction = (messageId, actionIndex) => {
    setActionState(messageId, actionIndex, { status: 'cancelled' });
  };

  const uploadAttachment = async (attachment) => {
    const followUp = input.trim();
    setMessages(messages => [...messages, { id: makeMessageId('user'), from: 'user', text: `Attached ${attachment.originalFilename || attachment.publicUrl || attachment.type || 'context'}` }]);
    try {
      const result = await api.assistant.uploadAttachment({ ...attachment, currentScreen, selectedEntity });
      const nextReferenceContext = assistantReferenceContextFromResult(attachment, result);
      if (nextReferenceContext) setReferenceContext(nextReferenceContext);
      setMessages(messages => [...messages, {
        id: makeMessageId('attachment-result'),
        from: 'ai',
        text: result?.message || 'Attachment processed by backend.',
        resultCard: {
          executed: result?.status !== 'unavailable',
          message: result?.message,
          result,
          action: { type: 'reference.upload_asset', title: 'Reference attachment' },
        },
      }]);
      const assetId = result?.asset?.id;
      if (followUp && assetId) {
        await sendAssistantMessage({
          text: referenceFollowUpPrompt(followUp, assetId),
          currentScreen,
          selectedEntity: { type: 'referenceAsset', id: assetId },
          setMessages,
          setInput,
          setExpanded,
          setThinking,
          appendUser: true,
        });
      }
    } catch (error) {
      setMessages(messages => [...messages, { id: makeMessageId('attachment-error'), from: 'ai', text: friendlyAssistantError(error), error: true }]);
    }
  };

  const uploadFileAsReference = async (file, notes = 'Dropped into Assistant') => {
    if (!isReferenceImageFile(file)) return;
    const dataUrl = await fileToDataUrl(file);
    await uploadAttachment({
      type: 'product_image',
      attachmentPurpose: 'product_image',
      dataUrl,
      originalFilename: file.name,
      mimeType: file.type || 'image/png',
      notes,
    });
  };

  const handleInputPaste = (event) => {
    const file = Array.from(event.clipboardData?.files || []).find(isReferenceImageFile);
    if (file) {
      event.preventDefault();
      uploadFileAsReference(file, 'Pasted into Assistant');
    }
  };

  const handleReferenceDrop = (event) => {
    event.preventDefault();
    setExpanded?.(true);
    const file = Array.from(event.dataTransfer?.files || []).find(isReferenceImageFile);
    if (file) {
      uploadFileAsReference(file, 'Dropped into Assistant');
      return;
    }
    const url = extractFirstHttpUrl(event.dataTransfer?.getData('text/uri-list') || event.dataTransfer?.getData('text/plain') || '');
    if (url) setInput(existing => existing.trim() ? `${existing.trim()} ${url}` : url);
  };

  return (
    <div className={'chatbar ' + (expanded ? 'chatbar-expanded' : '')} data-collapsed={String(!!sidebarCollapsed)}>
      {expanded && (
        <div className="chatbar-msgs-wrap">
          <div className="chatbar-msgs-head">
            <div className="row" style={{gap:10}}>
              <div className="asst-orb-mini"/>
              <div>
                <div style={{fontWeight:600, fontSize:13}}>{ASSISTANT_NAME}</div>
                <div className="muted" style={{fontSize:11}}>Context: {currentScreen}</div>
              </div>
            </div>
            <button className="btn btn-icon btn-ghost" onClick={() => setExpanded(false)} title="Minimize">
              <Icon name="x" size={14}/>
            </button>
          </div>
          <div className="chatbar-msgs" ref={scrollRef}>
            {messages.map((m) => (
              <div key={m.id} className={'chatbar-row ' + (m.from === 'ai' ? 'is-ai' : 'is-u')}>
                {m.from === 'ai' && <div className="chatbar-avatar"><div className="asst-orb-mini"/></div>}
                <AssistantBubble message={m} onRetry={(retryText) => send(retryText, { appendUser: false })} onCancel={cancelAction} onPreviewAction={previewAction} onConfirmAction={confirmAction} onQuickReply={sendQuickReply} selectedEntity={selectedEntity}/>
              </div>
            ))}
            {thinking && (
              <div className="chatbar-row is-ai">
                <div className="chatbar-avatar"><div className="asst-orb-mini"/></div>
                <div className="chatbar-bubble is-ai chatbar-thinking">
                  <span/><span/><span/>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="chatbar-bottom">
        <div className="chatbar-inner">
          {!expanded && (
            <AssistantActionTray currentScreen={currentScreen} selectedEntity={selectedEntity} open={actionTrayOpen && !thinking} onSelect={insertActionDraft} />
          )}
          {expanded && <AssistantActionTray currentScreen={currentScreen} selectedEntity={selectedEntity} open={actionTrayOpen && !thinking} onSelect={insertActionDraft} />}
          <div className="chatbar-input-row" onDrop={handleReferenceDrop} onDragOver={event => event.preventDefault()}>
            <button className="chatbar-orb-btn" onClick={() => setExpanded(!expanded)} title={ASSISTANT_NAME}>
              <div className="asst-orb-mini"/>
            </button>
            <button className={'chatbar-icon-btn ' + (actionTrayOpen ? 'is-active' : '')} title="Quick actions" onClick={() => { setExpanded(true); setActionTrayOpen(open => !open); }}><Icon name="sparkles" size={14}/></button>
            {pendingActionMetadata && (
              <button className="assistant-action-chip" type="button" onClick={() => setPendingActionMetadata(null)} title="Clear action">
                <span>{pendingActionMetadata.label || 'Draft action'}</span>
                <Icon name="x" size={11}/>
              </button>
            )}
            <AssistantReferenceChip context={referenceContext} onClear={() => setReferenceContext(null)} onUse={useReferenceContext} />
            <input
              ref={inputRef}
              className="chatbar-input"
              placeholder={assistantInputPlaceholder(pendingActionMetadata)}
              value={input}
              onChange={e => { setInput(e.target.value); if (!e.target.value.trim()) setPendingActionMetadata(null); }}
              onFocus={() => setExpanded(true)}
              onPaste={handleInputPaste}
              onKeyDown={e => { if (e.key === 'Enter' && !thinking) send(input); }}
            />
            <button className="chatbar-icon-btn" title="Attach context" onClick={() => setAttachmentOpen(true)}><Icon name="plus" size={14}/></button>
            <button className="chatbar-voice" onClick={onVoice} title="Live voice mode">
              <Icon name="mic" size={14}/>
              <span>Voice</span>
            </button>
            <button className="chatbar-send" onClick={() => send(input)} title="Send" disabled={thinking}>
              <Icon name="send" size={13}/>
            </button>
          </div>
        </div>
      </div>
      <AssistantAttachmentModal
        open={attachmentOpen}
        onClose={() => setAttachmentOpen(false)}
        onUpload={uploadAttachment}
      />
    </div>
  );
};

// ============= VOICE MODE =============
export const VoiceMode = ({ onClose }) => {
  const [listening, setListening] = useState(true);
  const [transcript, setTranscript] = useState('');
  const [aiSpeaking, setAiSpeaking] = useState(false);
  const [time, setTime] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTime(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Simulate voice conversation
  useEffect(() => {
    const phrases = [
      "Make a carousel about consistency for Instagram...",
      "Move Friday's post to Monday morning...",
      "Generate three TikTok hooks about productivity...",
    ];
    let i = 0;
    const cycle = () => {
      setListening(true);
      setAiSpeaking(false);
      let p = 0;
      const phrase = phrases[i % phrases.length];
      const typing = setInterval(() => {
        p++;
        setTranscript(phrase.slice(0, p));
        if (p >= phrase.length) {
          clearInterval(typing);
          setTimeout(() => {
            setListening(false);
            setAiSpeaking(true);
            setTimeout(() => {
              setTranscript('');
              i++;
              cycle();
            }, 2200);
          }, 600);
        }
      }, 50);
    };
    cycle();
  }, []);

  const fmtTime = (s) => `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

  return (
    <div className="voice-overlay">
      <div className="voice-stage">
        <div className="voice-bg-glow"/>
        <div className={'voice-orb ' + (aiSpeaking ? 'speaking' : 'listening')}>
          <div className="voice-orb-core"/>
          <div className="voice-orb-ring r1"/>
          <div className="voice-orb-ring r2"/>
          <div className="voice-orb-ring r3"/>
        </div>

        <div className="voice-status">
          {aiSpeaking ? 'AI is responding…' : listening ? 'Listening…' : 'Connecting…'}
        </div>

        <div className="voice-transcript">
          {transcript && <span className="voice-transcript-text">"{transcript}"</span>}
          {!transcript && aiSpeaking && <span className="voice-transcript-text muted">AI is generating your content...</span>}
        </div>

        <div className="voice-waveform">
          {Array.from({length: 32}).map((_,i) => (
            <div
              key={i}
              className="voice-wave-bar"
              style={{
                animationDelay: `${i * 0.04}s`,
                opacity: aiSpeaking ? 1 : listening ? 0.7 : 0.3
              }}
            />
          ))}
        </div>

        <div className="voice-controls">
          <button className="voice-btn voice-btn-mute" title="Mute">
            <Icon name="mic" size={20}/>
          </button>
          <button className="voice-btn voice-btn-end" onClick={onClose} title="End conversation">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" transform="rotate(135 12 12)"/></svg>
          </button>
          <button className="voice-btn voice-btn-text" onClick={onClose} title="Switch to text">
            <Icon name="x" size={20}/>
          </button>
        </div>

        <div className="voice-time">{fmtTime(time)}</div>
      </div>
    </div>
  );
};

// ============= AI PANEL (right-side drawer) =============
export const AIPanel = ({ open, onClose, currentScreen, selectedEntity = null, notificationContext = null, onVoice, navigate }) => {
  const [messages, setMessages] = useState(() => loadCachedAssistantPanelMessages());
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [actionTrayOpen, setActionTrayOpen] = useState(false);
  const [pendingActionMetadata, setPendingActionMetadata] = useState(null);
  const [referenceContext, setReferenceContext] = useState(null);
  const { hydrateContentItem, refreshContent } = useContentFeed();
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const lastNotificationRequestRef = useRef(null);

  const syncAssistantCreatedContent = async (resultLike) => {
    const batchId = generationBatchIdFromAssistantResult(resultLike);
    const ids = createdContentIdsFromAssistantResult(resultLike);
    if (ids.length) {
      await Promise.all(ids.map(id => hydrateContentItem(id).catch(() => null)));
    }
    await refreshContent({ background: true, refreshMetrics: false });
    if (batchId && typeof navigate === 'function') {
      navigate('create', { batchId, replace: false });
    }
  };

  useEffect(() => {
    if (open) {
      setMessages(existing => hasConversationMessages(existing) ? existing : [greetingMessage(currentScreen)]);
    }
  }, [open, currentScreen]);

  useEffect(() => {
    cacheAssistantPanelMessages(messages);
  }, [messages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  useEffect(() => {
    if (!open || !notificationContext?.id) return;
    const requestKey = notificationContext.requestId || `${notificationContext.id}:${notificationContext.assistantIntent || ''}`;
    if (lastNotificationRequestRef.current === requestKey) return;
    lastNotificationRequestRef.current = requestKey;
    const prompt = `Open recommendation: ${notificationContext.inputSummary || notificationContext.cta || 'Review this notification'}${notificationContext.assistantIntent ? ` Assistant intent: ${notificationContext.assistantIntent}.` : ''}`;
    sendAssistantMessage({
      text: prompt,
      currentScreen,
      selectedEntity: notificationContext,
      setMessages,
      setInput,
      setThinking,
      appendUser: true,
      onAssistantResult: syncAssistantCreatedContent,
    });
  }, [open, notificationContext, currentScreen]);

  const send = (txt, options = {}) => {
    const trimmed = txt.trim();
    const actionMetadata = pendingActionMetadataForMessage(trimmed, pendingActionMetadata, options);
    setActionTrayOpen(false);
    if (trimmed) setPendingActionMetadata(null);
    return sendAssistantMessage({
      text: txt,
      currentScreen,
      selectedEntity,
      actionMetadata,
      setMessages,
      setInput,
      setThinking,
      appendUser: options.appendUser !== false,
      onAssistantResult: syncAssistantCreatedContent,
    });
  };

  const sendQuickReply = (reply) => {
    const text = reply?.prompt || reply?.label || '';
    const actionMetadata = quickReplyActionMetadata(reply);
    return actionMetadata ? send(text, { actionMetadata }) : send(text);
  };

  const insertActionDraft = (draft) => {
    if (!draft?.metadata) return;
    setPendingActionMetadata(draft.metadata || null);
    setActionTrayOpen(false);
    window.requestAnimationFrame?.(() => inputRef.current?.focus());
  };

  const useReferenceContext = (kind) => {
    const draft = referenceActionDraftFromContext(referenceContext, kind);
    if (draft) insertActionDraft(draft);
  };

  const cancelConfirmation = (id) => {
    setMessages(messages => messages.map(message => message.id === id ? { ...message, cancelled: true } : message));
  };

  const setActionState = (messageId, actionIndex, state) => {
    setMessages(messages => messages.map(message => {
      if (message.id !== messageId) return message;
      const actionStates = { ...(message.actionStates || {}) };
      actionStates[actionIndex] = { ...(actionStates[actionIndex] || {}), ...state };
      return { ...message, actionStates };
    }));
  };

  const previewAction = async (messageId, actionIndex, action) => {
    setActionState(messageId, actionIndex, { status: 'previewing' });
    try {
      const preview = await api.assistant.previewAction({ action });
      setActionState(messageId, actionIndex, { status: 'previewed', preview });
    } catch (error) {
      setActionState(messageId, actionIndex, { status: 'error', message: friendlyAssistantError(error) });
    }
  };

  const confirmAction = async (messageId, actionIndex, action) => {
    setActionState(messageId, actionIndex, { status: 'confirming' });
    try {
      const result = await api.assistant.confirmAction({ action, confirmed: true });
      if (result?.assistantResponse) {
        setActionState(messageId, actionIndex, { status: 'confirmed', result: null });
        setMessages(messages => [...messages, assistantResponseToBubble(result.assistantResponse, result.message)]);
        notifyAssistantResult(syncAssistantCreatedContent, result.assistantResponse);
        return;
      }
      setActionState(messageId, actionIndex, { status: 'confirmed', result });
      notifyAssistantResult(syncAssistantCreatedContent, result);
    } catch (error) {
      setActionState(messageId, actionIndex, { status: 'error', message: friendlyAssistantError(error) });
    }
  };

  const cancelAction = (messageId, actionIndex) => {
    setActionState(messageId, actionIndex, { status: 'cancelled' });
  };

  const navigateFromResult = (target, options) => {
    if (typeof navigate === 'function') {
      navigate(target, options);
    }
  };

  const uploadAttachment = async (attachment) => {
    const followUp = input.trim();
    setMessages(messages => [...messages, { id: makeMessageId('user'), from: 'user', text: `Attached ${attachment.originalFilename || attachment.publicUrl || attachment.type || 'context'}` }]);
    try {
      const result = await api.assistant.uploadAttachment({ ...attachment, currentScreen, selectedEntity });
      const nextReferenceContext = assistantReferenceContextFromResult(attachment, result);
      if (nextReferenceContext) setReferenceContext(nextReferenceContext);
      setMessages(messages => [...messages, {
        id: makeMessageId('attachment-result'),
        from: 'ai',
        text: result?.message || 'Attachment processed by backend.',
        resultCard: {
          executed: result?.status !== 'unavailable',
          message: result?.message,
          result,
          action: { type: 'reference.upload_asset', title: 'Reference attachment' },
        },
      }]);
      const assetId = result?.asset?.id;
      if (followUp && assetId) {
        await sendAssistantMessage({
          text: referenceFollowUpPrompt(followUp, assetId),
          currentScreen,
          selectedEntity: { type: 'referenceAsset', id: assetId },
          setMessages,
          setInput,
          setThinking,
          appendUser: true,
        });
      }
    } catch (error) {
      setMessages(messages => [...messages, { id: makeMessageId('attachment-error'), from: 'ai', text: friendlyAssistantError(error), error: true }]);
    }
  };

  const uploadFileAsReference = async (file, notes = 'Dropped into Assistant') => {
    if (!isReferenceImageFile(file)) return;
    const dataUrl = await fileToDataUrl(file);
    await uploadAttachment({
      type: 'product_image',
      attachmentPurpose: 'product_image',
      dataUrl,
      originalFilename: file.name,
      mimeType: file.type || 'image/png',
      notes,
    });
  };

  const handleInputPaste = (event) => {
    const file = Array.from(event.clipboardData?.files || []).find(isReferenceImageFile);
    if (file) {
      event.preventDefault();
      uploadFileAsReference(file, 'Pasted into Assistant');
    }
  };

  const handleReferenceDrop = (event) => {
    event.preventDefault();
    const file = Array.from(event.dataTransfer?.files || []).find(isReferenceImageFile);
    if (file) {
      uploadFileAsReference(file, 'Dropped into Assistant');
      return;
    }
    const url = extractFirstHttpUrl(event.dataTransfer?.getData('text/uri-list') || event.dataTransfer?.getData('text/plain') || '');
    if (url) setInput(existing => existing.trim() ? `${existing.trim()} ${url}` : url);
  };

  const language = assistantUiLanguage();
  const placeholder = assistantInputPlaceholder(pendingActionMetadata, language);
  const showQuickChips = !thinking && messages.length <= 1 && messages.every(m => m.from === 'ai' && !m.response && !m.resultCard);
  const showTrayInDock = actionTrayOpen && !thinking;

  useEffect(() => {
    if (!open) return undefined;
    const handler = (event) => { if (event.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const submitInput = () => {
    if (!input.trim() || thinking) return;
    send(input);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitInput();
    }
  };

  return (
    <>
      <div className={'as-bd ' + (open ? 'is-open' : '')} onClick={onClose}/>
      <aside className={'as-panel ' + (open ? 'is-open' : '')} role="dialog" aria-label="AI Assistant">
        <header className="as-panel-h">
          <div className="as-panel-h-l">
            <span className="as-panel-avatar">
              <img src="/assets/dathent-logo.png" alt="" width="28" height="28" style={{ borderRadius: '50%', display: 'block' }}/>
              <span className="as-panel-online"/>
            </span>
            <div>
              <div className="as-panel-title">Dathent <span className="as-grad-text">Assistant</span></div>
              <div className="as-panel-sub">context: {currentScreen || 'app'}</div>
            </div>
          </div>
          <div className="as-panel-h-r">
            <button type="button" className="btn btn-icon btn-ghost btn-sm" title="Voice mode" onClick={onVoice}>
              <Icon name="mic" size={13}/>
            </button>
            <button type="button" className="btn btn-icon btn-ghost btn-sm" title="Close" onClick={onClose}>
              <Icon name="x" size={13}/>
            </button>
          </div>
        </header>

        <div className="as-thread" ref={scrollRef} onDrop={handleReferenceDrop} onDragOver={event => event.preventDefault()}>
          {messages.map((m) => (
            m.from === 'ai' ? (
              <div key={m.id} className="as-msg as-msg-bot">
                <span className="as-avatar">
                  <img src="/assets/dathent-logo.png" alt="" width="24" height="24" style={{ borderRadius: '50%', display: 'block' }}/>
                </span>
                <AssistantBubble
                  message={m}
                  variant="panel"
                  onRetry={(retryText) => send(retryText, { appendUser: false })}
                  onCancel={cancelAction}
                  onPreviewAction={previewAction}
                  onConfirmAction={confirmAction}
                  onQuickReply={sendQuickReply}
                  selectedEntity={selectedEntity}
                  onNavigate={navigateFromResult}
                />
              </div>
            ) : (
              <div key={m.id} className="as-msg as-msg-user">
                <AssistantBubble
                  message={m}
                  variant="panel"
                  onRetry={(retryText) => send(retryText, { appendUser: false })}
                  onCancel={cancelAction}
                  onPreviewAction={previewAction}
                  onConfirmAction={confirmAction}
                  onQuickReply={sendQuickReply}
                  selectedEntity={selectedEntity}
                  onNavigate={navigateFromResult}
                />
              </div>
            )
          ))}
          {thinking && (
            <div className="as-msg as-msg-bot">
              <span className="as-avatar">
                <img src="/assets/dathent-logo.png" alt="" width="24" height="24" style={{ borderRadius: '50%', display: 'block' }}/>
              </span>
              <div className="as-bubble as-bubble-bot as-typing">
                <span className="as-tdot"/><span className="as-tdot"/><span className="as-tdot"/>
              </div>
            </div>
          )}
          {showQuickChips && <AssistantQuickChips language={language} onPick={(action) => send(action.prompt)} />}
        </div>

        <div className="as-dock">
          {showTrayInDock && (
            <div style={{ marginBottom: 8 }}>
              <AssistantActionTray currentScreen={currentScreen} selectedEntity={selectedEntity} open={showTrayInDock} onSelect={insertActionDraft} />
            </div>
          )}
          {(pendingActionMetadata || referenceContext) && (
            <div className="as-dock-meta">
              {pendingActionMetadata && (
                <button className="assistant-action-chip" type="button" onClick={() => setPendingActionMetadata(null)} title="Clear action">
                  <span>{pendingActionMetadata.label || 'Draft action'}</span>
                  <Icon name="x" size={11}/>
                </button>
              )}
              <AssistantReferenceChip context={referenceContext} onClear={() => setReferenceContext(null)} onUse={useReferenceContext} />
            </div>
          )}
          <div className="as-dock-textarea">
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={e => { setInput(e.target.value); if (!e.target.value.trim()) setPendingActionMetadata(null); }}
              onPaste={handleInputPaste}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoFocus={open}
            />
            <div className="as-dock-tools">
              <button type="button" title="Quick actions" onClick={() => setActionTrayOpen(state => !state)}>
                <Icon name="sparkles" size={14}/>
              </button>
              <button type="button" title="Attach context" onClick={() => setAttachmentOpen(true)}>
                <Icon name="plus" size={14}/>
              </button>
              <button type="button" className="as-send" title="Send" disabled={!input.trim() || thinking} onClick={submitInput}>
                <Icon name="send" size={13}/>
              </button>
            </div>
          </div>
          <div className="as-dock-foot">
            <span className="mono">⌘K</span>
            <span>to toggle assistant</span>
          </div>
        </div>
      </aside>
      <AssistantAttachmentModal
        open={attachmentOpen}
        onClose={() => setAttachmentOpen(false)}
        onUpload={uploadAttachment}
      />
    </>
  );
};
